import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { useAppDispatch, useSelector } from '../../../../store/hooks';
import { selectAppConfigurationSearchQuery } from '../../../../store/features/appConfiguration/appConfigurationSlice';
import {
    selectFlightNumbersPanel,
    loadFlightNumbers,
    searchFlightNumbers,
    deleteFlightNumbers,
} from '../../../../store/features/flightNumbers/flightNumbersSlice';
import {
    Spinner,
    TableColumnDefinition,
    ToolbarButton,
    createTableColumn,
    Button,
    TableCellLayout,
    Tooltip,
    MessageBar,
} from '@fluentui/react-components';
import { Add24Regular, Delete24Regular, Edit24Regular } from '@fluentui/react-icons';
import FlightNumberDialogAction from './components/FlightNumberDialogAction';
import { useFlightNumbersPanelStyles } from './FlightNumbersPanel.styles';
import ConfigurationTable from '../../components/ConfigurationTable/ConfigurationTable';
import { IConfigRow } from '../../components/ConfigurationTable/ConfigurationTable.utils';
import { safeDateCompare } from '../../../../utils/activeConversationsUtils';
import { HaulType } from '../../../../model';
import ConfirmDeletionDialog from '../../components/ConfirmDeletionDialog/ConfirmDeletionDialog';
import { getHaulTypeLabels } from '../../../DepartureBoardPage/utils/departureBoardFiltersUtils';

interface FlightNumberRow extends IConfigRow {
    flightNumber: string;
    haulType?: HaulType;
    isUserCreated: boolean;
    expiry: string;
}

const getColumns = (
    t: TFunction<'translation', undefined>,
    onEdit: (row: FlightNumberRow) => void,
    onDelete: (row: FlightNumberRow) => void
): TableColumnDefinition<FlightNumberRow>[] => {
    const haulTypeLabels = getHaulTypeLabels(t);
    return [
        createTableColumn<FlightNumberRow>({
            columnId: 'flightNumber',
            renderHeaderCell: () => 'Flight Number',
            renderCell: (i) => i.flightNumber,
            compare: (a, b) => a.value.localeCompare(b.value, undefined, {
                sensitivity: 'base',
            }),
        }),
        createTableColumn<FlightNumberRow>({
            columnId: 'permanent',
            renderHeaderCell: () => t('TableHeader_Permanent'),
            renderCell: (i) => (i.expiry === '-' ? 'Yes' : 'No'),
            compare: (a, b) => a.value.localeCompare(b.value, undefined, {
                sensitivity: 'base',
            }),
        }),
        createTableColumn<FlightNumberRow>({
            columnId: 'expiry',
            renderHeaderCell: () => t('TableHeader_Expires'),
            renderCell: (i) => i.expiry,
            compare: (a, b) => safeDateCompare(a.expiry, b.expiry)
        }),
        createTableColumn<FlightNumberRow>({
            columnId: 'haulType',
            renderHeaderCell: () => t('TableHeader_HaulType', 'SH/LH'),
            renderCell: (i) => haulTypeLabels[i.haulType ?? ''] ?? '-',
            compare: (a, b) => (a.haulType ?? '').localeCompare(b.haulType ?? '', undefined, {
                sensitivity: 'base',
            }),
        }),
        createTableColumn<FlightNumberRow>({
            columnId: 'actions',
            renderHeaderCell: () => <></>,
            renderCell: (item) => (
                <TableCellLayout>
                    {item.isUserCreated && (
                        <Tooltip content={t('Tooltip_Edit')} relationship="label">
                            <Button
                                appearance="transparent"
                                icon={<Edit24Regular />}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onEdit(item);
                                }}
                            />
                        </Tooltip>
                    )}
                    {item.isUserCreated && (
                        <Tooltip content={t('Tooltip_Delete')} relationship="label">
                            <Button
                                appearance="transparent"
                                icon={<Delete24Regular />}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onDelete(item);
                                }}
                            />
                        </Tooltip>
                    )}
                </TableCellLayout>
            ),
        })
    ];
};

const FlightNumbersPanel: React.FC = () => {
    const { t } = useTranslation();
    const styles = useFlightNumbersPanelStyles();
    const dispatch = useAppDispatch();

    const searchQuery = useSelector(selectAppConfigurationSearchQuery);
    const { items, loadStatus } = useSelector(selectFlightNumbersPanel);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedFlightNumber, setSelectedFlightNumber] = useState('');
    const [selectedPermanent, setSelectedPermanent] = useState(false);
    const [selectedHaulType, setSelectedHaulType] = useState<HaulType>('Short');
    const [deletionTarget, setDeletionTarget] = useState<FlightNumberRow | null>(null);

    const onEdit = useCallback((row: FlightNumberRow) => {
        setSelectedFlightNumber(row.flightNumber);
        setSelectedPermanent(row.expiry === '-');
        setSelectedHaulType(row.haulType ?? 'Short');
        setDialogOpen(true);
    }, []);

    const onDelete = useCallback((row: FlightNumberRow) => setDeletionTarget(row), []);

    const columns = useMemo<TableColumnDefinition<FlightNumberRow>[]>(
        () => getColumns(t, onEdit, onDelete),
        [t, onEdit, onDelete]
    );

    useEffect(() => {
        dispatch(loadFlightNumbers());
    }, [dispatch]);
    useEffect(() => {
        dispatch(searchFlightNumbers(searchQuery));
    }, [dispatch, searchQuery]);

    const rows = useMemo(
        () =>
            items.map((i) => {
                const isPermanent =
                    !i.expireOnUtc ||
                    (i.expireOnUtc &&
                        new Date(i.expireOnUtc).getFullYear() === 9999);

                return {
                    id: i.flightNumber,
                    value: i.flightNumber,
                    flightNumber: i.flightNumber,
                    haulType: i.haulType,
                    isUserCreated: i.isUserCreated,
                    expiry: isPermanent
                        ? '-'
                        : i.expireOnUtc
                            ? new Date(i.expireOnUtc)
                                .toISOString()
                                .substring(0, 16)
                                .replace('T', ' ')
                            : '-',
                };
            }),
        [items]
    );

    return (
        <>
            <MessageBar className={styles.messageBar}>
                {t('AppConfiguration_FlightNumbers_MsgBar24Hours')}
            </MessageBar>
            <ConfigurationTable
                items={rows}
                columns={columns}
                loadStatus={loadStatus}
                ariaLabel="flightNumbersTable"
                pagination={{
                    currentPage: 1,
                    pageSize: rows.length,
                    totalCount: rows.length,
                }}
                bottomActions={
                    <ToolbarButton
                        icon={<Add24Regular />}
                        onClick={() => {
                            setSelectedFlightNumber('');
                            setSelectedPermanent(false);
                            setSelectedHaulType('Short');
                            setDialogOpen(true);
                        }}
                    >
                        {t('ToolbarButton_AddFlightNumber')}
                    </ToolbarButton>
                }
            />
            <FlightNumberDialogAction
                open={dialogOpen}
                flightNumber={selectedFlightNumber}
                isPermanent={selectedPermanent}
                haulType={selectedHaulType}
                onSaved={() => setDialogOpen(false)}
                onOpenChange={(_, d) => {
                    setDialogOpen(d.open);
                    if (!d.open) {
                        setSelectedFlightNumber('');
                        setSelectedPermanent(false);
                        setSelectedHaulType('Short');
                    }
                }}
            />
            <ConfirmDeletionDialog
                open={!!deletionTarget}
                message={deletionTarget ? `Delete flight number ${deletionTarget.flightNumber}?` : ''}
                onConfirm={() => {
                    if (deletionTarget) {
                        dispatch(deleteFlightNumbers(deletionTarget.flightNumber));
                        setDeletionTarget(null);
                    }
                }}
                onClose={() => setDeletionTarget(null)}
            />
            {loadStatus === 'loading' && (
                <Spinner className={styles.spinner} appearance="primary" />
            )}
        </>
    );
};

export default FlightNumbersPanel;
